import React from 'react';
import { DispatchProp, shallowEqual, useSelector } from 'react-redux';
import { AsyncSwitch, DelayedRedirect } from 'src/core/engine/router';
import { Route } from 'react-router';
import { useLazyRegister } from 'src/core/engine/hooks/useRegisterFeature';
import { DefaultMeta } from 'src/components/AppShell/Head';

import { Container, Wrapper } from '../../components/Layout';
import { PageMethodConnected } from '../../components/CustomisationPage/pages/Method';
import { PageTypeConnected } from '../../components/CustomisationPage/pages/Type';
import { PagePositionConnected } from '../../components/CustomisationPage/pages/Position';
import { PageArtworkConnected } from '../../components/CustomisationPage/pages/Artwork';
import { PageConfigureConnected } from '../../components/CustomisationPage/pages/Configure';
import { LayoutCustomisation } from '../../components/Layout/LayoutCustomisation';
import { SummaryConnected } from '../../components/CustomisationPage/Summary';
import { CustomisationLogin } from '../../components/CustomisationPage/CustomisationLogin';
import { StoreState } from '../../types/global-types';
import { paths } from '../../config';
import { customisationRegister } from '../../components/CustomisationPage/feature/customisation.register';

import '../../scss/_root-component.scss';

import { helmet } from './customisation';

type Props = {
    id: number;
    pathname: string;
    outdated: boolean;
} & DispatchProp<any>;

const ANCHOR = 'customisation';

export const Customisation: React.FC<Props> = () => {
    const ready = useLazyRegister(customisationRegister, true, 'Customisation');
    if (!ready) return null;

    if (window.location.pathname.includes('/customisation/')) window.location.hash = ANCHOR;

    return (
        <Wrapper variant="page-inner" id={ANCHOR}>
            <Container variant="narrow">
                <DefaultMeta data={helmet} />
                <LayoutCustomisation
                    login={<CustomisationLogin />}
                    main={
                        <AsyncSwitch base="/customisation">
                            <Route path={paths.customisation.position} element={<PagePositionConnected />} />
                            <Route path={paths.customisation.method} element={<PageMethodConnected />} />
                            <Route path={paths.customisation.artwork} element={<PageArtworkConnected />} />
                            <Route path={paths.customisation.type} element={<PageTypeConnected />} />
                            <Route path={paths.customisation.configure} element={<PageConfigureConnected />} />
                            <Route path="*" element={<CustomisationRedirect />} />
                        </AsyncSwitch>
                    }
                    side={<SummaryConnected />}
                />
            </Container>
        </Wrapper>
    );
};

const resolvingSelector = (x: StoreState) => x.router.resolving;
function CustomisationRedirect() {
    const resolving = useSelector(resolvingSelector, shallowEqual);
    if (resolving) return null;
    return <DelayedRedirect to="/" />;
}

export default Customisation;
